/**
 * CountryBadge — the one country chip. Phase 3a, Part 3.
 *
 * ===========================================================================
 * WHAT IT IS FOR, AND WHY IT LOOKS REDUNDANT TODAY
 * ===========================================================================
 * The role remodel introduces two ALL-COUNTRY roles (Developer, Dev Head). For
 * them a record surface is a mixed list, and "which company is this?" stops
 * being answerable from the chrome at the top of the page. The badge answers it
 * per record.
 *
 * For a single-country operator it is redundant, and that is the intended cost:
 * one chip on a row they already know the country of, in exchange for the
 * cross-country reader never having to guess. The alternative — showing it only
 * to all-country users — would mean the two populations see different screens,
 * so a bug reported by one could not be reproduced by the other.
 *
 * ===========================================================================
 * THE CODE COMES FROM THE ROUTE, NOT FROM THE RECORD, AND THAT IS HONEST
 * ===========================================================================
 * NO DTO IN THIS APP CARRIES A COUNTRY CODE. `InquiryDto`, `QuoteDto`,
 * `SalesOrderDto`, `PartyDto` and the line/tax rows all carry `country_id:
 * number` — an opaque server integer — and the client has no id→code map:
 * `GET /countries` needs `config.manage`, which is exactly why
 * `domain/countries.ts` exists at all.
 *
 * What the client DOES have is the route. Every in-app path is
 * `/countries/:cc/…`, `RequireCountry` refuses an unknown or out-of-scope code
 * before anything renders, and the API enforces the same scope with
 * `@CountryScoped`. So on `/countries/IN/quotes` every row IS an Indian quote —
 * the badge is DERIVED from a guarded fact, not invented, and it cannot
 * disagree with the data because the same string fetched the data.
 *
 * The prop is nevertheless `code`, required, and every call site passes
 * `useCountryCode()` explicitly. When a DTO one day carries its own code — which
 * is what a genuine cross-country list needs — the change is at the call site
 * and this component does not move.
 *
 * ===========================================================================
 * "IN" IS NOT THE ENGLISH WORD "IN"
 * ===========================================================================
 * A screen reader voices a bare `IN` as the preposition and `AE` as two letters.
 * So the badge is `role="img"` with a label naming the country — the exact
 * pattern `Sidebar`'s active-country pill already ships and `sidebar-brand.test`
 * already asserts. The code stays the visible text because that is how this app
 * has written a country since Phase 2 (the switcher, the sidebar, the admin role
 * chip) and because two glyphs is what fits on a table row.
 *
 * THE BRIEF SPELLS THE UAE BADGE "UAE"; THIS RENDERS "AE". `AE` is the ISO code
 * in `COUNTRY_ORDER`, in `/countries/AE/…`, in the switcher, in the sidebar and
 * in every admin role chip. A badge that alone said "UAE" would be the one place
 * in the app where the country's identifier is spelled differently from the URL
 * it was read out of, and it would break the "consistent sizing" requirement by
 * being 50% wider than the other four. The full name is not lost: it is what the
 * accessible label says, for all five.
 *
 * Colour, contrast and the reason the fill is opaque: `--crf-country-badge-height`
 * in `styles/tokens.css`. Ratios: `styles/tokens.contrast.test.ts`.
 */

import { COUNTRIES } from '../../domain/countries';
import type { CountryCode } from '../../domain/countries';
import { cn } from '../../lib/cn';

/**
 * The badge's geometry and colour, in one string, exported so a test can assert
 * that every surface wears the SAME one rather than a similar one.
 *
 * `h-country-badge` is the token (`--spacing-country-badge`); no size, colour or
 * radius is spelled as a literal anywhere in this file.
 */
export const COUNTRY_BADGE_CLASS =
  'inline-flex h-country-badge shrink-0 items-center justify-center rounded-pill border border-fg-muted bg-surface px-1.5 font-mono text-mono leading-none text-fg-secondary';

export interface CountryBadgeProps {
  code: CountryCode;
  /**
   * The words before the country name in the accessible label — "Country: India",
   * "Active country: India". Defaults to the plain noun; the sidebar passes its
   * own so the pill it replaces keeps the exact label its test asserts.
   */
  label?: string;
  className?: string;
}

export function CountryBadge({
  code,
  label = 'Country',
  className,
}: CountryBadgeProps): React.JSX.Element {
  return (
    <span
      role="img"
      aria-label={`${label}: ${COUNTRIES[code].name}`}
      className={cn(COUNTRY_BADGE_CLASS, className)}
    >
      {code}
    </span>
  );
}
